import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RealtimeMount } from "@/lib/realtime";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import InvoicePrint from "@/pages/InvoicePrint";
import InvoiceList from "@/components/invoices/InvoiceList";
import InvoiceEditor from "@/components/invoices/InvoiceEditor";
import ServiceDue from "@/components/invoices/ServiceDue";
import Reminders from "@/components/invoices/Reminders";
import RouteSetup from "@/components/routes/RouteSetup";
import StatementPrint from "@/pages/StatementPrint";
import DepositSlip from "@/pages/DepositSlip";
import StickerPrint from "@/pages/StickerPrint";
import StickerDesigner from "@/pages/StickerDesigner";
import InvoiceLayoutEditor from "@/pages/InvoiceLayoutEditor";
import AdminPanel from "@/pages/AdminPanel";
import AuthCallback from "@/pages/AuthCallback";
import AcceptInvite from "@/pages/AcceptInvite";
import Privacy from "@/pages/Privacy";
import Customers from "@/components/Customers";
import FilesManager from "@/components/FilesManager";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="app-shell flex items-center justify-center" data-testid="auth-loading">
        <Loader2 className="animate-spin text-primary" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function AdminOnly({ children }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/" replace />;
}

function Perm({ k, children }) {
  const { can } = useAuth();
  return can(k) ? children : <Navigate to="/" replace />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/db/:dbId" element={<Protected><Dashboard /></Protected>} />
          <Route path="/db/:dbId/:table" element={<Protected><Dashboard /></Protected>} />
          <Route path="/invoices" element={<Protected><Perm k="invoices"><Dashboard><InvoiceList /></Dashboard></Perm></Protected>} />
          <Route path="/invoices/print-list" element={<Protected><InvoicePrint /></Protected>} />
          <Route path="/customers" element={<Protected><Perm k="customers"><Dashboard><Customers /></Dashboard></Perm></Protected>} />
          <Route path="/files" element={<Protected><Dashboard><FilesManager /></Dashboard></Protected>} />
          <Route path="/invoices/new" element={<Protected><Perm k="invoices"><Dashboard><InvoiceEditor /></Dashboard></Perm></Protected>} />
          <Route path="/invoices/:invoiceId" element={<Protected><Perm k="invoices"><Dashboard><InvoiceEditor /></Dashboard></Perm></Protected>} />
          <Route path="/invoices/:invoiceId/print" element={<Protected><InvoicePrint /></Protected>} />
          <Route path="/invoices/batch/:batchId/print" element={<Protected><InvoicePrint /></Protected>} />
          <Route path="/service-due" element={<Protected><Perm k="service_due"><Dashboard><ServiceDue /></Dashboard></Perm></Protected>} />
          <Route path="/routes" element={<Protected><Perm k="routes"><Dashboard><RouteSetup /></Dashboard></Perm></Protected>} />
          <Route path="/sticker-designer" element={<Protected><Perm k="stickers"><Dashboard><StickerDesigner /></Dashboard></Perm></Protected>} />
          <Route path="/reminders" element={<Protected><Perm k="reminders"><Dashboard><Reminders /></Dashboard></Perm></Protected>} />
          <Route path="/reminders/print" element={<Protected><Reminders print /></Protected>} />
          <Route path="/statement" element={<Protected><StatementPrint /></Protected>} />
          <Route path="/deposit-slip" element={<Protected><DepositSlip /></Protected>} />
          <Route path="/stickers" element={<Protected><StickerPrint /></Protected>} />
          <Route path="/invoice-layout" element={<Protected><AdminOnly><Dashboard><InvoiceLayoutEditor /></Dashboard></AdminOnly></Protected>} />
          <Route path="/techs" element={<Protected><AdminOnly><Dashboard><AdminPanel /></Dashboard></AdminOnly></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <RealtimeMount />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" richColors />
    </AuthProvider>
  );
}
